import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { indicatorSetApi } from '../../services/api';
import type { IndicatorSetListItem, IndicatorSetFilters } from '../../types';
import { IndicatorConfigurator } from '../common/IndicatorConfigurator';
import { indicatorApi } from '../../services/api';

const IndicatorSetList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<IndicatorSetFilters>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setToDelete, setSetToDelete] = useState<number | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [setToDuplicate, setSetToDuplicate] = useState<number | null>(null);
  const [newSetName, setNewSetName] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: '',
    category: '',
    tags: [] as string[],
    indicators: [] as any[],
  });
  const [currentIndicator, setCurrentIndicator] = useState<any>(null);
  const [newTag, setNewTag] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(!isMobile);

  // Fetch indicator sets
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['indicator-sets', filters, page, rowsPerPage],
    queryFn: () => indicatorSetApi.list(filters),
  });

  // Fetch categories for filter
  const { data: categories } = useQuery({
    queryKey: ['indicator-set-categories'],
    queryFn: indicatorSetApi.getCategories,
  });

  // Fetch available indicators
  const { data: availableIndicators = {} } = useQuery<Record<string, any>>({
    queryKey: ['available-indicators'],
    queryFn: indicatorApi.getAvailable,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: indicatorSetApi.delete,
    onSuccess: () => {
      toast.success('Indicator set deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['indicator-sets'] });
      setDeleteDialogOpen(false);
    },
    onError: () => {
      toast.error('Failed to delete indicator set');
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      indicatorSetApi.duplicate(id, name),
    onSuccess: () => {
      toast.success('Indicator set duplicated successfully');
      queryClient.invalidateQueries({ queryKey: ['indicator-sets'] });
      setDuplicateDialogOpen(false);
      setNewSetName('');
    },
    onError: () => {
      toast.error('Failed to duplicate indicator set');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: indicatorSetApi.create,
    onSuccess: () => {
      toast.success('Indicator set created successfully');
      queryClient.invalidateQueries({ queryKey: ['indicator-sets'] });
      setCreateDialogOpen(false);
      // Reset form
      setCreateFormData({
        name: '',
        description: '',
        category: '',
        tags: [],
        indicators: [],
      });
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || 'Failed to create indicator set';
      toast.error(errorMsg);
    },
  });

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: event.target.value });
    setPage(0);
  };

  const handleCategoryChange = (event: any) => {
    setFilters({ ...filters, category: event.target.value });
    setPage(0);
  };

  const handleDelete = (id: number) => {
    setSetToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (setToDelete) {
      deleteMutation.mutate(setToDelete);
    }
  };

  const handleDuplicate = (id: number, name: string) => {
    setSetToDuplicate(id);
    setNewSetName(`${name}_copy`);
    setDuplicateDialogOpen(true);
  };

  const confirmDuplicate = () => {
    if (setToDuplicate && newSetName) {
      duplicateMutation.mutate({ id: setToDuplicate, name: newSetName });
    }
  };

  const handleCreateIndicatorSet = () => {
    if (!createFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (createFormData.indicators.length === 0) {
      toast.error('At least one indicator is required');
      return;
    }
    createMutation.mutate(createFormData);
  };

  const handleAddIndicator = () => {
    if (!currentIndicator || !currentIndicator.indicator_name?.trim()) {
      toast.error('Please configure an indicator');
      return;
    }
    setCreateFormData({
      ...createFormData,
      indicators: [...createFormData.indicators, { ...currentIndicator }],
    });
    setCurrentIndicator(null);
  };

  const handleRemoveIndicator = (index: number) => {
    setCreateFormData({
      ...createFormData,
      indicators: createFormData.indicators.filter((_, i) => i !== index),
    });
  };

  const handleAddTag = () => {
    if (newTag.trim() && !createFormData.tags.includes(newTag.trim())) {
      setCreateFormData({
        ...createFormData,
        tags: [...createFormData.tags, newTag.trim()],
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setCreateFormData({
      ...createFormData,
      tags: createFormData.tags.filter((t) => t !== tag),
    });
  };

  const indicatorSets = data?.results || [];

  return (
    <Box>
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between" 
        alignItems={isMobile ? 'stretch' : 'center'}
        gap={2}
        mb={3}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'}>Indicator Presets</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          fullWidth={isMobile}
        >
          {isMobile ? 'New Preset' : 'New Indicator Preset'}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {isMobile && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={filtersExpanded ? 2 : 0}>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              size="small"
            >
              {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Collapse in={!isMobile || filtersExpanded}>
          <Box 
            display="flex" 
            flexDirection={isMobile ? 'column' : 'row'}
            gap={2}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={filters.search || ''}
              onChange={handleSearchChange}
              fullWidth={isMobile}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                style: { fontSize: 16 }
              }}
              sx={{ flexGrow: isMobile ? undefined : 1 }}
            />

            <FormControl size="small" sx={{ minWidth: isMobile ? undefined : 200 }} fullWidth={isMobile}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ''}
                onChange={handleCategoryChange}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories?.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isMobile && (
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Mobile Card View */}
      {isMobile ? (
        <Box>
          <Grid container spacing={2}>
            {isLoading ? (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography>Loading...</Typography>
                </Paper>
              </Grid>
            ) : indicatorSets.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No indicator sets found</Typography>
                </Paper>
              </Grid>
            ) : (
              indicatorSets.map((set) => (
                <Grid size={{ xs: 12 }} key={set.id}>
                  <Card>
                    <CardActionArea onClick={() => navigate(`/ai/indicator-sets/${set.id}`)}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <TimelineIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle1" fontWeight="medium">
                            {set.name}
                          </Typography>
                        </Box>
                        {set.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {set.description}
                          </Typography>
                        )}
                        <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                          {set.indicator_names.slice(0, 2).map((name, idx) => (
                            <Chip
                              key={idx}
                              label={name}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                          {set.indicator_names.length > 2 && (
                            <Chip
                              label={`+${set.indicator_names.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    <Box display="flex" justifyContent="flex-end" px={1} pb={1} gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(set.id, set.name);
                        }}
                      >
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(set.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
          <Paper sx={{ mt: 2 }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data?.count || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Per page:"
            />
          </Paper>
        </Box>
      ) : (
      /* Desktop Table */
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Indicators</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : indicatorSets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No indicator sets found
                </TableCell>
              </TableRow>
            ) : (
              indicatorSets.map((set) => (
                <TableRow
                  key={set.id}
                  hover
                  onClick={() => navigate(`/ai/indicator-sets/${set.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TimelineIcon fontSize="small" color="primary" />
                      <Typography variant="body2" fontWeight="medium">
                        {set.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {set.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {set.indicator_names.slice(0, 3).map((name, idx) => (
                        <Chip
                          key={idx}
                          label={name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                      {set.indicator_names.length > 3 && (
                        <Chip
                          label={`+${set.indicator_names.length - 3}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {set.category && (
                      <Chip label={set.category} size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {set.tags.slice(0, 2).map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                      {set.tags.length > 2 && (
                        <Chip
                          label={`+${set.tags.length - 2}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {set.usage_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/ai/indicator-sets/${set.id}`);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(set.id, set.name);
                        }}
                      >
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(set.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={data?.count || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          Are you sure you want to delete this indicator set? This action cannot be
          undone.
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog
        fullScreen={isMobile}
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
      >
        <DialogTitle>Duplicate Indicator Set</DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New Set Name"
            fullWidth
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
            InputProps={{ style: { fontSize: 16 } }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDuplicate}
            color="primary"
            variant="contained"
            disabled={!newSetName || duplicateMutation.isPending}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>Create New Indicator Set</DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <Box sx={{ pt: 2 }}>
            {/* Basic Info */}
            <TextField
              label="Name *"
              fullWidth
              margin="normal"
              value={createFormData.name}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, name: e.target.value })
              }
              InputProps={{ style: { fontSize: 16 } }}
            />
            
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={createFormData.description}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, description: e.target.value })
              }
            />

            <TextField
              label="Category"
              fullWidth
              margin="normal"
              value={createFormData.category}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, category: e.target.value })
              }
            />

            {/* Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                {createFormData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Box>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  label="Add Tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button onClick={handleAddTag} variant="outlined" size="small">
                  Add
                </Button>
              </Box>
            </Box>

            {/* Indicators */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Indicators *
              </Typography>
              
              {/* Existing Indicators */}
              {createFormData.indicators.map((indicator, index) => (
                <Paper key={index} sx={{ p: 2, mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {indicator.indicator_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Params: {JSON.stringify(indicator.params)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveIndicator(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}

              {/* Add New Indicator */}
              <Paper sx={{ p: 2, mt: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Indicator
                </Typography>
                <IndicatorConfigurator
                  availableIndicators={availableIndicators}
                  value={currentIndicator}
                  onChange={setCurrentIndicator}
                  showOutputInfo={true}
                />
                <Button
                  onClick={handleAddIndicator}
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="small"
                  sx={{ mt: 2 }}
                  disabled={!currentIndicator?.indicator_name}
                  fullWidth={isMobile}
                >
                  Add Indicator
                </Button>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateIndicatorSet}
            color="primary"
            variant="contained"
            disabled={createMutation.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IndicatorSetList;
